import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import cx from 'classnames';
import styles from './NotificationList.module.scss';
import { useTranslation } from 'react-i18next';
import NotificationListItem from '../NotificationListItem/NotificationListItem';
import { useMessageContext } from '../../../../context/MessageContext';
import Ref from '../../../../components/Ref/Ref';
import { LoadingSpinner } from '../../../../components/Icon/icons';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import DropdownItem from '../DropdownItem/DropdownItem';
import Icon from '../../../../components/Icon/Icon';
import openMessages from '../../../../assets/openMessages.svg';
import SidebarButton from '../../../../routes/newVideo/VideoSidebarMenu/SidebarButton/SidebarButton';
import { FILTER_ALL, FILTER_READ, FILTER_UNREAD } from '../../../../constants/notificationMessages';
import { CustomButton } from '../../../../components/Button/Button';
import PageHeader from 'engage/components/PageHeader/PageHeader';

const CHECKED = 1;
const UNCHECKED = 2;
const INDETERMINATE = -1;
const MAX_MESSAGES_PER_PAGE = 50;

export default function NotificationList() {
  const { t } = useTranslation();
  const {
    loading,
    messageLoading,
    actionLoading,
    scrollRef,
    unreadMessagesCount,
    sortedMessages,
    setMessageDetails,
    filterUnread,
    deleteMessage,
    selectedFilter,
    setSelectedFilter,
    selectedItems,
    setSelectedItems,
    setShowNotificationDetail,
    deleteSelectedMessages,
    markAsReadSelectedMessages,
    totalMessagesCount,
    markAsReadAllMessages,
    deleteAllMessages,
    showReleaseAnnouncement,
  } = useMessageContext();

  const myRef = useRef(null);

  useOutsideClick(myRef, () => setIsActiveDropdown(false));
  const [isActiveDropdown, setIsActiveDropdown] = useState(false);

  const [allSelected, setAllSelected] = useState(false);
  const checkRef = useRef(null);
  const [status, setStatus] = useState(UNCHECKED);
  const [toTotalMessagesCount, setToTotalMessagesCount] = useState(false);

  const totalMessagesCountByFilter = useMemo(() => {
    switch (selectedFilter.ROLE) {
      case FILTER_ALL.ROLE:
        return totalMessagesCount;
      case FILTER_UNREAD.ROLE:
        return unreadMessagesCount;
      default:
        return totalMessagesCount - unreadMessagesCount;
    }
  }, [selectedFilter, totalMessagesCount, unreadMessagesCount]);

  const checkBoxAll = () => {
    if (allSelected) {
      setSelectedItems([]);
      setStatus(UNCHECKED);
    } else {
      setSelectedItems(sortedMessages.map(({ id, read }) => ({ id, read })));
      setStatus(CHECKED);
    }
    setAllSelected(!allSelected);
  };

  useEffect(() => {
    if (selectedItems.length === sortedMessages.length && selectedItems.length > 0) {
      setAllSelected(true);
      setStatus(CHECKED);
    } else if (selectedItems.length > 0) {
      toTotalMessagesCount && setToTotalMessagesCount(false);
      setStatus(INDETERMINATE);
      setAllSelected(false);
    } else {
      setStatus(UNCHECKED);
      setAllSelected(false);
      toTotalMessagesCount && setToTotalMessagesCount(false);
    }
  }, [selectedItems, sortedMessages]);

  useEffect(() => {
    if (toTotalMessagesCount && status === INDETERMINATE && sortedMessages.length !== selectedItems.length) {
      setSelectedItems(sortedMessages.map(({ id, read }) => ({ id, read })));
    }
  }, [status, toTotalMessagesCount]);

  const toggleActive = () => {
    setIsActiveDropdown(!isActiveDropdown);
  };

  const clickFilter = item => {
    setIsActiveDropdown(false);
    setAllSelected(false);
    setSelectedFilter(item);
    setSelectedItems([]);
    setToTotalMessagesCount(false);
  };

  const readSelectedClicked = useCallback(() => {
    if (toTotalMessagesCount) {
      markAsReadAllMessages();
      return;
    }
    markAsReadSelectedMessages();
  }, [toTotalMessagesCount, selectedItems]);

  const deleteSelectedClicked = useCallback(() => {
    if (toTotalMessagesCount) {
      deleteAllMessages();
      return;
    }
    deleteSelectedMessages();
  }, [toTotalMessagesCount, selectedItems]);

  const readAllClicked = useCallback(() => {
    if (showReleaseAnnouncement) {
      markAsReadSelectedMessages(sortedMessages);
    } else {
      markAsReadAllMessages();
    }
  }, [selectedItems]);

  useEffect(() => {
    if (totalMessagesCount > 0 && checkRef.current) {
      checkRef.current.checked = status === CHECKED;
      checkRef.current.indeterminate = status === INDETERMINATE;
    }
  }, [status]);

  useEffect(() => {
    if (!selectedItems.length && toTotalMessagesCount) {
      setToTotalMessagesCount(false);
    }
  }, [selectedItems]);

  return (
    <Ref innerRef={scrollRef}>
      <div className={cx(styles.notificationList)}>
        <div className={cx(styles.header)}>
          <div className={cx(styles.titleContainer)}>
            <PageHeader
              title={t(showReleaseAnnouncement ? 'notification.announcements' : 'notification.title')}
              className={styles.title}
              info={showReleaseAnnouncement ? false : (
                  <>
                    {messageLoading ? (
                      <p>{t('general.loading')}</p>
                    ) : (
                        <>
                        {t('notification.you_have')}{' '}
                        <span className={unreadMessagesCount > 0 ? styles.green : ''}>
                          {' '}
                          {unreadMessagesCount} {t('notification.unread')}
                        </span>{' '}
                        {t(unreadMessagesCount === 1 ? 'notification.notification' : 'notification.notifications')}
                        </>
                    )}
                  </>
              )} />
            <div className={styles.headerWrapper}>
              <div className={styles.actionContainer}>
                <div className={styles.selectAndFilterBox} ref={myRef}>
                  <div
                    className={cx(styles.checkbox, {
                      [styles.allSelectedActive]: allSelected,
                      [styles.disabled]: messageLoading || actionLoading || sortedMessages.length === 0,
                    })}
                    onClick={checkBoxAll}
                  >
                    <input ref={checkRef} type="checkbox" id="myCheck" checked={allSelected} aria-label={t('general.select')} />
                  </div>
                  <div
                    className={cx(
                      styles.dropdownContainer,
                      { [styles.disabled]: messageLoading || actionLoading || totalMessagesCount === 0 },
                      { [styles.activeSelectAndFilterBox]: isActiveDropdown }
                    )}
                    onClick={toggleActive}
                  >
                    <div className={styles.filterName}>{t(selectedFilter.TEXT)}</div>
                    <div className={styles.dropdownBox}>
                      <Icon className={cx(styles.arrowIcon, { [styles.isActive]: isActiveDropdown })} src="arrowDown" />
                    </div>
                    {isActiveDropdown && (
                      <div className={styles.dropdown}>
                        <DropdownItem filter={FILTER_ALL} handleClick={clickFilter} selectedFilter={selectedFilter} />
                        <DropdownItem filter={FILTER_UNREAD} handleClick={clickFilter} selectedFilter={selectedFilter} />
                        <DropdownItem filter={FILTER_READ} handleClick={clickFilter} selectedFilter={selectedFilter} />
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={cx(styles.actionsContainer, {
                    [styles.disabled]: messageLoading,
                    [styles.show]: selectedItems.length > 0,
                  })}
                >
                  {selectedItems.some(({ read }) => !read) && (
                    <SidebarButton
                      visible={true}
                      icon="envelope"
                      onClick={readSelectedClicked}
                      disabled={messageLoading || actionLoading}
                      hoverText={t('notification.tooltip.read')}
                      classNameButton={styles.icon}
                      classNameTooltip={styles.titleTooltip}
                      classNameWrapper={styles.customWrapper}
                    />
                  )}
                  <SidebarButton
                    visible={true}
                    icon="trash"
                    onClick={deleteSelectedClicked}
                    disabled={messageLoading || actionLoading}
                    hoverText={t('notification.tooltip.trash')}
                    classNameButton={styles.icon}
                    classNameTooltip={styles.titleTooltip}
                    classNameWrapper={styles.customWrapper}
                  />
                  <div
                    className={cx(styles.countContainer, {
                      [styles.show]: allSelected && totalMessagesCountByFilter > MAX_MESSAGES_PER_PAGE && selectedItems.length > 0,
                      [styles.disabled]: messageLoading || actionLoading,
                    })}
                  >
                    <span>
                      {t('notifications.actions.selected_count', {
                        amount: toTotalMessagesCount ? totalMessagesCountByFilter : selectedItems.length,
                      })}
                    </span>
                    <CustomButton
                      variant="link"
                      label={t('notifications.actions.select_all_messages', {
                        amount: totalMessagesCountByFilter,
                      })}
                      className={cx(styles.button, { [styles.show]: !toTotalMessagesCount })}
                      onClick={() => setToTotalMessagesCount(true)}
                    />
                  </div>
                </div>
              </div>
              <div
                className={cx(styles.actionsAllContainer, {
                  [styles.disabled]:
                    messageLoading ||
                    actionLoading ||
                    sortedMessages.length === 0 ||
                    unreadMessagesCount === 0 ||
                    (showReleaseAnnouncement && !sortedMessages.some(message => !message.read)),
                })}
              >
                <SidebarButton
                  visible={true}
                  onClick={readAllClicked}
                  label={t('notification.read_all')}
                  classNameButton={styles.icon}
                  classNameTooltip={styles.titleTooltip}
                />
              </div>
            </div>
            {messageLoading ? (
              <h4>{t('general.loading')}</h4>
            ) : (
              <>
                {sortedMessages.length === 0 ? (
                  <div className={cx(styles.noNotificationLeft)}>
                    <img src={openMessages} alt="No new messages SVG" />
                    <h4>{t('notification.no_notification_left')}</h4>
                  </div>
                ) : (
                  <div className={cx(styles.list, { [styles.disabled]: actionLoading })}>
                    {sortedMessages?.map(message => (
                      <NotificationListItem
                        key={message.id}
                        message={message}
                        allSelected={allSelected}
                        selectedItems={selectedItems}
                        setSelectedItems={setSelectedItems}
                        setMessageDetails={setMessageDetails}
                        filterUnread={filterUnread}
                        deleteMessage={deleteMessage}
                        setShowNotificationDetail={setShowNotificationDetail}
                        emptyList={sortedMessages.length === 0}
                      />
                    ))}
                    {loading && sortedMessages.length > 0 && (
                      <div className={styles.spinnerContainer}>
                        <LoadingSpinner theme="light" />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Ref>
  );
}
